/* HARPAH high-converting cart system — no external dependencies. */

(() => {
  'use strict';

  if (customElements.get('h-cart')) return;

  const FLASH_KEY = 'h-cart-flash';
  const OFFER_STATE_VERSION = 1;
  const OFFER_RECONNECT_GRACE_MS = 15000;
  const LEGACY_GIFT_PROPERTY = '_h_cart_free_gift';
  const REWARD_GIFT_PROPERTY = '_harpah_free_gift';

  class HCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.sectionId = this.dataset.sectionId;
      this.cartAddUrl = this.dataset.cartAddUrl;
      this.cartChangeUrl = this.dataset.cartChangeUrl;
      this.cartUpdateUrl = this.dataset.cartUpdateUrl;
      this.cartUrl = this.dataset.cartUrl;
      this.isBusy = false;
      this.noteTimer = null;
      this.offerTimer = null;
      this.toastTimer = null;
      this.giftSyncTimer = null;
      this.giftSyncAttempts = 0;
      this.giftActionInProgress = false;
      this.abortController = new AbortController();

      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('click', this.handleClick.bind(this), listenerOptions);
      this.addEventListener('change', this.handleChange.bind(this), listenerOptions);
      this.addEventListener('input', this.handleInput.bind(this), listenerOptions);
      this.addEventListener('submit', this.handleSubmit.bind(this), listenerOptions);

      this.restoreFlash();
      this.syncAllVariantSelectors();
      this.setupMobileDock();
      this.setupTimedOffer();
      this.setupAutoGift();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.noteTimer);
      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.giftSyncTimer);
      window.clearInterval(this.offerTimer);
      this.checkoutObserver?.disconnect();
    }

    handleClick(event) {
      const quantityButton = event.target.closest('[data-cart-quantity-button]');
      if (quantityButton && this.contains(quantityButton)) {
        event.preventDefault();
        if (this.isBusy) return;
        const quantity = Math.max(0, Number.parseInt(quantityButton.dataset.quantity, 10) || 0);
        this.changeLine(quantityButton.dataset.lineKey, quantity, quantityButton.closest('[data-cart-line]'));
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton && this.contains(removeButton)) {
        event.preventDefault();
        if (this.isBusy) return;
        this.changeLine(removeButton.dataset.lineKey, 0, removeButton.closest('[data-cart-line]'));
        return;
      }

      const checkoutButton = event.target.closest('[name="checkout"]');
      if (checkoutButton && this.contains(checkoutButton)) {
        if (checkoutButton.dataset.rewardVerified === 'true') {
          this.dispatchCartEvent('hcart:checkout', {
            itemCount: Number.parseInt(this.dataset.cartItemCount, 10) || 0
          });
          return;
        }

        event.preventDefault();
        if (this.isBusy || this.giftActionInProgress) return;
        this.verifyRewardsThenCheckout(checkoutButton);
      }
    }

    handleChange(event) {
      const quantityInput = event.target.closest('[data-cart-quantity-input]');
      if (quantityInput && this.contains(quantityInput)) {
        if (this.isBusy) return;
        const quantity = Math.max(0, Number.parseInt(quantityInput.value, 10) || 0);
        this.changeLine(quantityInput.dataset.lineKey, quantity, quantityInput.closest('[data-cart-line]'));
        return;
      }

      const variantSelect = event.target.closest('[data-upsell-variant]');
      if (variantSelect && this.contains(variantSelect)) {
        this.syncVariantSelector(variantSelect);
      }
    }

    handleInput(event) {
      const note = event.target.closest('[data-cart-note]');
      if (!note || !this.contains(note)) return;

      const status = this.querySelector('[data-note-status]');
      if (status) status.textContent = 'Saving…';

      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => this.saveNote(note.value, status), 550);
    }

    handleSubmit(event) {
      const upsellForm = event.target.closest('[data-upsell-form]');
      if (upsellForm && this.contains(upsellForm)) {
        event.preventDefault();
        if (!this.isBusy) this.addUpsell(upsellForm);
        return;
      }

      const discountForm = event.target.closest('[data-discount-form]');
      if (discountForm && this.contains(discountForm)) {
        event.preventDefault();
        if (!this.isBusy) this.applyCustomerDiscount(discountForm);
      }
    }

    async changeLine(lineKey, quantity, lineElement) {
      this.clearLineError(lineElement);
      this.setLoading(true);

      try {
        const response = await this.postJson(this.cartChangeUrl, {
          id: lineKey,
          quantity,
          sections: this.getSectionIds(),
          sections_url: this.cartUrl
        });

        this.storeFlash(this.dataset.cartUpdatedMessage || 'Your bag has been updated.');
        this.renderResponse(response);
        this.dispatchCartEvent('hcart:updated', { action: quantity === 0 ? 'remove' : 'quantity', quantity });
      } catch (error) {
        this.showLineError(lineElement, error.message);
        this.announce(error.message);
        this.setLoading(false);
      }
    }

    async addUpsell(form) {
      if (!form.reportValidity()) return;

      const submitButton = form.querySelector('[data-upsell-submit]');
      const errorElement = form.querySelector('[data-upsell-error]');
      const productTitle = form.dataset.productTitle || 'Item';
      this.hideMessage(errorElement);
      this.setButtonLoading(submitButton, true);
      this.setLoading(true);

      try {
        const formData = new FormData(form);
        formData.set('sections', this.getSectionIds().join(','));
        formData.set('sections_url', this.cartUrl);

        const response = await this.postForm(this.cartAddUrl, formData);
        const message = `${productTitle}: ${this.dataset.itemAddedMessage || 'Added to your bag.'}`;
        this.storeFlash(message);
        this.renderResponse(response);
        this.dispatchCartEvent('hcart:upsell_added', { productTitle });
      } catch (error) {
        this.showMessage(errorElement, error.message, true);
        this.announce(error.message);
        this.setButtonLoading(submitButton, false);
        this.setLoading(false);
      }
    }

    async saveNote(note, statusElement) {
      try {
        await this.postJson(this.cartUpdateUrl, { note });
        if (statusElement) statusElement.textContent = 'Saved';
      } catch (error) {
        if (statusElement) statusElement.textContent = 'Could not save. Please try again.';
      }
    }

    async applyCustomerDiscount(form) {
      const input = form.querySelector('[data-discount-input]');
      const submitButton = form.querySelector('[data-discount-submit]');
      const messageElement = form.querySelector('[data-discount-message]');
      const code = input?.value.trim();
      if (!code) {
        this.showMessage(messageElement, 'Enter a promo code.', true);
        input?.focus();
        return;
      }

      this.setLoading(true);
      if (submitButton) submitButton.disabled = true;
      this.hideMessage(messageElement);

      try {
        const response = await this.updateDiscountSet({ add: [code], remove: [] });
        if (!this.responseContainsDiscount(response, code)) {
          throw new Error('This code is invalid or is not eligible for the items in your bag.');
        }

        const timedCode = this.dataset.timedOfferCode?.trim();
        if (
          timedCode &&
          this.normalizeCode(timedCode) !== this.normalizeCode(code) &&
          !this.responseContainsDiscount(response, timedCode)
        ) {
          const storageKey = this.dataset.timedOfferKey;
          const state = this.readOfferState(storageKey);
          if (state) {
            state.status = 'invalid';
            this.writeOfferState(storageKey, state);
          }
        }

        this.storeFlash(`Promo code ${code.toUpperCase()} applied.`);
        this.renderResponse(response);
        this.dispatchCartEvent('hcart:discount_applied', { code });
      } catch (error) {
        this.showMessage(messageElement, error.message, true);
        this.announce(error.message);
        this.setLoading(false);
        if (submitButton) submitButton.disabled = false;
      }
    }

    syncAllVariantSelectors() {
      this.querySelectorAll('[data-upsell-variant]').forEach((select) => this.syncVariantSelector(select));
    }

    syncVariantSelector(select) {
      const form = select.closest('[data-upsell-form]');
      if (!form) return;

      const button = form.querySelector('[data-upsell-submit]');
      const label = form.querySelector('[data-upsell-button-label]');
      const price = form.closest('[data-upsell-card]')?.querySelector('[data-upsell-price]');
      const option = select.selectedOptions[0];
      const hasSelection = Boolean(select.value && option && !option.disabled);
      const addLabel = button?.dataset.addLabel || 'Add';
      const chooseLabel = button?.dataset.chooseLabel || 'Choose option';

      if (button) button.disabled = !hasSelection;
      if (label) label.textContent = hasSelection ? `${addLabel} · ${option.dataset.price || ''}` : chooseLabel;

      if (price && hasSelection) {
        const fragments = [];
        if (option.dataset.comparePrice) {
          const compare = document.createElement('s');
          compare.textContent = option.dataset.comparePrice;
          fragments.push(compare);
        }
        const current = document.createElement('span');
        current.textContent = option.dataset.price || '';
        fragments.push(current);
        price.replaceChildren(...fragments);
      }
    }

    async verifyRewardsThenCheckout(button) {
      this.setLoading(true);

      try {
        const gifts = this.getConfiguredGiftTiers();
        const cart = await this.getCartJson();
        const managedLines = this.getManagedGiftLines(cart);
        const variantsInCart = new Set(
          managedLines.map((line) => Number(line.variant_id || line.id))
        );

        const missing = gifts.filter((gift) => (
          gift.unlocked && !variantsInCart.has(gift.variantId)
        ));

        if (missing.length) {
          await this.postJson(this.cartAddUrl, {
            items: missing.map((gift) => ({
              id: gift.variantId,
              quantity: 1,
              properties: {
                [REWARD_GIFT_PROPERTY]: 'true',
                [LEGACY_GIFT_PROPERTY]: 'true',
                _harpah_gift_tier: String(gift.tier),
                _harpah_gift_label: gift.label
              }
            }))
          });
        }

        button.dataset.rewardVerified = 'true';
        button.click();
      } catch (error) {
        console.warn('[HARPAH Cart] Checkout reward verification failed:', error.message);
        this.showToast('We could not verify your unlocked gifts. Please try checkout again.');
      } finally {
        if (this.isConnected) this.setLoading(false);
      }
    }

    setupMobileDock() {
      const dock = this.querySelector('[data-mobile-dock]');
      const mainCheckout = this.querySelector('[data-main-checkout]');
      if (!dock || !mainCheckout || !('IntersectionObserver' in window)) return;

      this.checkoutObserver = new IntersectionObserver((entries) => {
        const checkoutIsVisible = entries.some((entry) => entry.isIntersecting);
        dock.classList.toggle('is-hidden', checkoutIsVisible);
      }, { threshold: 0.2 });

      this.checkoutObserver.observe(mainCheckout);
    }

    setupAutoGift() {
      if (this.dataset.threeGiftRewards !== 'true') return;
      this.queueAutoGiftSync(0);
    }

    queueAutoGiftSync(delay = 0) {
      window.clearTimeout(this.giftSyncTimer);
      this.giftSyncTimer = window.setTimeout(() => {
        if (!this.isConnected) return;

        if (this.isBusy || this.offerActionInProgress || this.giftActionInProgress) {
          this.giftSyncAttempts += 1;
          if (this.giftSyncAttempts <= 20) this.queueAutoGiftSync(250);
          return;
        }

        this.giftSyncAttempts = 0;
        this.syncAutoGift();
      }, delay);
    }

    getConfiguredGiftTiers() {
      return [1,2,3].map((tier) => ({
        tier,
        variantId: Number.parseInt(this.getAttribute(`data-gift-${tier}-variant-id`), 10) || 0,
        unlocked: this.getAttribute(`data-gift-${tier}-unlocked`) === 'true',
        inCart: this.getAttribute(`data-gift-${tier}-in-cart`) === 'true',
        label: this.getAttribute(`data-gift-${tier}-label`) || `Gift ${tier}`
      })).filter((gift) => gift.variantId > 0);
    }

    getManagedGiftLines(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];

      return items.filter((line) => {
        const props = line?.properties || {};
        return (
          String(props[REWARD_GIFT_PROPERTY] || '').toLowerCase() === 'true' ||
          String(props[LEGACY_GIFT_PROPERTY] || '').toLowerCase() === 'true'
        );
      });
    }

    async syncAutoGift() {
      if (this.giftActionInProgress) return;
      this.giftActionInProgress = true;

      try {
        const gifts = this.getConfiguredGiftTiers();
        if (!gifts.length) return;

        const cart = await this.getCartJson();
        const managedLines = this.getManagedGiftLines(cart);
        const updates = {};

        // Remove reward gifts whose threshold is no longer unlocked.
        managedLines.forEach((line) => {
          const tier = Number.parseInt(line?.properties?._harpah_gift_tier, 10);
          const config = gifts.find((gift) => gift.tier === tier);

          if (config && !config.unlocked) {
            updates[line.key] = 0;
          }
        });

        if (Object.keys(updates).length) {
          this.setLoading(true);
          const response = await this.updateGiftLines(updates);
          this.renderResponse(response);
          this.dispatchCartEvent('hcart:gifts_removed', { reason: 'threshold' });
          return;
        }

        const variantsInCart = new Set(
          managedLines.map((line) => Number(line.variant_id || line.id))
        );

        const missing = gifts.filter((gift) => (
          gift.unlocked && !variantsInCart.has(gift.variantId)
        ));

        if (!missing.length) return;

        this.setLoading(true);

        const response = await this.postJson(this.cartAddUrl, {
          items: missing.map((gift) => ({
            id: gift.variantId,
            quantity: 1,
            properties: {
              [REWARD_GIFT_PROPERTY]: 'true',
              [LEGACY_GIFT_PROPERTY]: 'true',
              _harpah_gift_tier: String(gift.tier),
              _harpah_gift_label: gift.label
            }
          })),
          sections: this.getSectionIds(),
          sections_url: this.cartUrl
        });

        this.storeFlash(
          missing.length > 1
            ? 'Your unlocked gifts were added automatically.'
            : 'Your unlocked gift was added automatically.'
        );

        this.renderResponse(response);
        this.dispatchCartEvent('hcart:gifts_added', {
          tiers: missing.map((gift) => gift.tier)
        });
      } catch (error) {
        console.warn('[HARPAH Cart] Reward gifts could not be synchronized:', error.message);
        this.showToast('Your unlocked gift could not be added automatically. Please refresh and try again.');
      } finally {
        this.giftActionInProgress = false;
        if (this.isConnected) this.setLoading(false);
      }
    }

    updateGiftLines(updates) {
      return this.postJson(this.cartUpdateUrl, {
        updates,
        sections: this.getSectionIds(),
        sections_url: this.cartUrl
      });
    }

    setupTimedOffer() {
      const eligible = this.dataset.timedOfferEligible === 'true';
      const code = this.dataset.timedOfferCode?.trim();
      const durationSeconds = Number.parseInt(this.dataset.timedOfferSeconds, 10) || 0;
      const card = this.querySelector('[data-timed-offer-card]');

      if (!eligible || !code || durationSeconds <= 0) return;
      if (!this.storageAvailable('localStorage')) {
        if (card) card.hidden = true;
        return;
      }

      const storageKey = this.dataset.timedOfferKey;
      let state = this.readOfferState(storageKey);
      const now = Date.now();

      if (!state) {
        state = {
          version: OFFER_STATE_VERSION,
          code,
          status: 'active',
          expiresAt: now + (durationSeconds * 1000)
        };
        this.writeOfferState(storageKey, state);
      }

      if (state.status === 'expired') {
        if (card) card.hidden = true;
        if (state.removalAttempted !== true) this.expireTimedOffer(storageKey, state, code);
        return;
      }

      if (state.expiresAt <= now) {
        this.expireTimedOffer(storageKey, state, code);
        return;
      }

      if (state.status === 'invalid') {
        if (card) card.hidden = true;
        return;
      }

      if (this.dataset.timedOfferApplied === 'true') {
        state.applied = true;
        state.appliedAt = now;
        this.writeOfferState(storageKey, state);
        if (card) card.hidden = false;
        this.startOfferCountdown(storageKey, state, code);
      } else if (
        state.applied === true &&
        Number.isFinite(state.appliedAt) &&
        now - state.appliedAt < OFFER_RECONNECT_GRACE_MS
      ) {
        if (card) card.hidden = false;
        this.startOfferCountdown(storageKey, state, code);
      } else {
        this.verifyTimedOffer(storageKey, state, code, card);
      }
    }

    async verifyTimedOffer(storageKey, state, code, card) {
      if (this.offerActionInProgress) return;
      this.offerActionInProgress = true;
      let isApplied = false;

      try {
        const cart = await this.getCartJson();
        isApplied = this.responseContainsDiscount(cart, code);
      } catch (error) {
        console.warn('[HARPAH Cart] Timed offer could not be verified:', error.message);
        if (card) card.hidden = true;
        return;
      } finally {
        this.offerActionInProgress = false;
      }

      if (isApplied) {
        state.applied = true;
        state.appliedAt = Date.now();
        this.writeOfferState(storageKey, state);
        if (card) card.hidden = false;
        this.startOfferCountdown(storageKey, state, code);
        return;
      }

      state.applied = false;
      this.writeOfferState(storageKey, state);
      if (this.isConnected) this.applyTimedOffer(storageKey, state, code);
    }

    async applyTimedOffer(storageKey, state, code) {
      if (this.offerActionInProgress) return;
      this.offerActionInProgress = true;
      this.setLoading(true);

      try {
        const response = await this.updateDiscountSet({ add: [code], remove: [] });
        if (!this.responseContainsDiscount(response, code)) {
          throw new Error('Configured timed discount is not eligible for this cart.');
        }

        state.status = 'active';
        state.applied = true;
        state.appliedAt = Date.now();
        state.removalAttempted = false;
        this.writeOfferState(storageKey, state);
        const rendered = this.renderResponse(response);
        if (!rendered && this.isConnected) {
          const card = this.querySelector('[data-timed-offer-card]');
          if (card) card.hidden = false;
          this.startOfferCountdown(storageKey, state, code);
        }
        this.dispatchCartEvent('hcart:offer_applied', { code, expiresAt: state.expiresAt });
      } catch (error) {
        state.status = 'invalid';
        this.writeOfferState(storageKey, state);
        const card = this.querySelector('[data-timed-offer-card]');
        if (card) card.hidden = true;
        this.setLoading(false);
        console.warn('[HARPAH Cart] Timed offer could not be applied:', error.message);
      } finally {
        this.offerActionInProgress = false;
        if (this.isConnected) this.setLoading(false);
      }
    }

    startOfferCountdown(storageKey, state, code) {
      const timerElement = this.querySelector('[data-offer-timer]');
      if (!timerElement) return;

      const update = () => {
        const remaining = Math.max(0, Math.ceil((state.expiresAt - Date.now()) / 1000));
        const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
        const seconds = String(remaining % 60).padStart(2, '0');
        timerElement.textContent = `${minutes}:${seconds}`;
        timerElement.setAttribute('aria-label', `${minutes} minutes ${seconds} seconds remaining`);

        if (remaining <= 0) {
          window.clearInterval(this.offerTimer);
          this.expireTimedOffer(storageKey, state, code);
        }
      };

      update();
      this.offerTimer = window.setInterval(update, 1000);
    }

    async expireTimedOffer(storageKey, state, code) {
      if (this.offerActionInProgress) return;
      this.offerActionInProgress = true;
      this.setLoading(true);

      const card = this.querySelector('[data-timed-offer-card]');
      if (card) card.hidden = true;

      state.status = 'expired';
      state.applied = false;
      state.appliedAt = null;
      state.removalAttempted = true;
      this.writeOfferState(storageKey, state);

      try {
        const response = await this.updateDiscountSet({ add: [], remove: [code] });
        this.renderResponse(response);
        this.dispatchCartEvent('hcart:offer_expired', { code });
      } catch (error) {
        console.warn('[HARPAH Cart] Expired offer could not be removed automatically:', error.message);
      } finally {
        this.offerActionInProgress = false;
        if (this.isConnected) this.setLoading(false);
      }
    }

    async updateDiscountSet({ add = [], remove = [] }) {
      const cart = await this.getCartJson();
      const removeKeys = remove.map((code) => this.normalizeCode(code));
      const existingCodes = this.getDiscountCodes(cart);
      const codes = existingCodes
        .filter((code) => !removeKeys.includes(this.normalizeCode(code)));

      add.forEach((code) => {
        if (!codes.some((existing) => this.normalizeCode(existing) === this.normalizeCode(code))) {
          codes.push(code);
        }
      });

      const existingKeys = existingCodes.map((code) => this.normalizeCode(code));
      const nextKeys = codes.map((code) => this.normalizeCode(code));
      const changed = (
        existingKeys.length !== nextKeys.length ||
        existingKeys.some((key, index) => key !== nextKeys[index])
      );

      if (!changed) return { ...cart, __hCartNoChange: true };

      return this.postJson(this.cartUpdateUrl, {
        discount: codes.join(','),
        sections: this.getSectionIds(),
        sections_url: this.cartUrl
      });
    }

    getDiscountCodes(cart) {
      const discountCodes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
      const explicitCodes = discountCodes
        .filter((discount) => discount?.code && discount.applicable !== false)
        .map((discount) => discount.code);

      if (explicitCodes.length > 0) return explicitCodes;

      const applications = [
        ...(Array.isArray(cart?.discount_applications) ? cart.discount_applications : []),
        ...(Array.isArray(cart?.cart_level_discount_applications) ? cart.cart_level_discount_applications : [])
      ];
      const fallbackCodes = [];

      applications.forEach((application) => {
        if (!application?.title || application.type !== 'discount_code') return;
        if (fallbackCodes.some((existing) => this.normalizeCode(existing) === this.normalizeCode(application.title))) return;
        fallbackCodes.push(application.title);
      });

      return fallbackCodes;
    }

    responseContainsDiscount(response, code) {
      const expected = this.normalizeCode(code);
      return this.getDiscountCodes(response).some((existing) => this.normalizeCode(existing) === expected);
    }

    normalizeCode(code) {
      return String(code || '').trim().toLowerCase();
    }

    readOfferState(key) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const state = JSON.parse(raw);
        if (state.version !== OFFER_STATE_VERSION || !Number.isFinite(state.expiresAt)) return null;
        return state;
      } catch (_error) {
        return null;
      }
    }

    writeOfferState(key, state) {
      try {
        window.localStorage.setItem(key, JSON.stringify(state));
      } catch (_error) {
        // Offer remains disabled when durable browser storage is unavailable.
      }
    }

    storageAvailable(type) {
      try {
        const storage = window[type];
        const testKey = '__h_cart_storage_test__';
        storage.setItem(testKey, testKey);
        storage.removeItem(testKey);
        return true;
      } catch (_error) {
        return false;
      }
    }

    async getCartJson() {
      const url = new URL(this.cartUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const data = await this.parseResponse(response);
      if (!response.ok) throw new Error(this.extractError(data));
      return data;
    }

    async postJson(url, payload) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      const data = await this.parseResponse(response);
      if (!response.ok) throw new Error(this.extractError(data));
      return data;
    }

    async postForm(url, formData) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await this.parseResponse(response);
      if (!response.ok) throw new Error(this.extractError(data));
      return data;
    }

    async parseResponse(response) {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) return response.json();
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (_error) {
        return { description: text };
      }
    }

    extractError(data) {
      return data?.description || data?.message || this.dataset.generalErrorMessage || 'We could not update your bag. Please try again.';
    }

    getSectionIds() {
      const ids = [this.sectionId, 'cart-icon-bubble'];
      document.querySelectorAll('[data-h-cart-render-section]').forEach((element) => {
        if (element.dataset.sectionId) ids.push(element.dataset.sectionId);
      });
      return [...new Set(ids.filter(Boolean))].slice(0, 5);
    }

    renderResponse(response) {
      if (response?.__hCartNoChange) {
        this.setLoading(false);
        return false;
      }

      const sections = response?.sections;
      if (!sections || !sections[this.sectionId]) {
        console.warn('[HARPAH Cart] Section HTML was missing; automatic reload was blocked to prevent a refresh loop.');
        this.setLoading(false);
        this.showToast('Your bag was updated. Refresh once if the latest totals are not visible.');
        return false;
      }

      const ids = Object.keys(sections).filter((id) => id !== this.sectionId);
      ids.push(this.sectionId);

      ids.forEach((id) => {
        const markup = sections[id];
        if (!markup) return;

        const parser = new DOMParser();
        const parsed = parser.parseFromString(markup, 'text/html');
        const sectionSelector = `shopify-section-${id}`;
        const sourceSection = parsed.getElementById(sectionSelector);
        const currentSection = document.getElementById(sectionSelector);

        if (sourceSection && currentSection) {
          currentSection.innerHTML = sourceSection.innerHTML;
        } else if (id === this.sectionId) {
          const currentMain = this.closest('.shopify-section');
          const parsedMain = parsed.querySelector('h-cart');
          if (currentMain && parsedMain) currentMain.innerHTML = parsed.body.innerHTML;
        }
      });

      let itemCount = Number(response.item_count);
      if (!Number.isFinite(itemCount)) {
        const renderedCart = document.getElementById(`HCart-${this.sectionId}`);
        itemCount = Number(renderedCart?.dataset.cartItemCount);
      }
      if (Number.isFinite(itemCount)) this.updateVisibleCartCounts(itemCount);

      window.setTimeout(() => {
        const currentCart = document.getElementById(`HCart-${this.sectionId}`);
        if (currentCart && typeof currentCart.queueAutoGiftSync === 'function') {
          currentCart.queueAutoGiftSync(80);
        }
      }, 0);

      return true;
    }

    updateVisibleCartCounts(itemCount) {
      document.querySelectorAll('[data-h-cart-count], [data-cart-count]').forEach((element) => {
        element.textContent = String(itemCount);
      });
    }

    setLoading(loading) {
      this.isBusy = loading;
      this.classList.toggle('is-loading', loading);
      this.setAttribute('aria-busy', String(loading));
      this.querySelectorAll('[name="checkout"]').forEach((button) => {
        button.disabled = loading;
      });
    }

    setButtonLoading(button, loading) {
      if (!button) return;
      button.classList.toggle('is-loading', loading);
      button.disabled = loading;
      button.setAttribute('aria-busy', String(loading));
    }

    showLineError(lineElement, message) {
      const errorElement = lineElement?.querySelector('[data-line-error]');
      this.showMessage(errorElement, message, true);
    }

    clearLineError(lineElement) {
      const errorElement = lineElement?.querySelector('[data-line-error]');
      this.hideMessage(errorElement);
    }

    showMessage(element, message, isError = false) {
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
      if (isError) element.setAttribute('role', 'alert');
    }

    hideMessage(element) {
      if (!element) return;
      element.hidden = true;
      element.textContent = '';
    }

    announce(message) {
      const region = this.querySelector('[data-cart-status]');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(() => { region.textContent = message; }, 30);
    }

    storeFlash(message) {
      try {
        window.sessionStorage.setItem(FLASH_KEY, JSON.stringify({ message, createdAt: Date.now() }));
      } catch (_error) {
        // The cart still updates when session storage is unavailable.
      }
    }

    restoreFlash() {
      try {
        const raw = window.sessionStorage.getItem(FLASH_KEY);
        if (!raw) return;
        window.sessionStorage.removeItem(FLASH_KEY);
        const flash = JSON.parse(raw);
        if (!flash.message || Date.now() - flash.createdAt > 10000) return;
        this.showToast(flash.message);
      } catch (_error) {
        // Ignore malformed or blocked storage.
      }
    }

    showToast(message) {
      const toast = this.querySelector('[data-cart-toast]');
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      this.announce(message);
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => {
        toast.hidden = true;
        toast.textContent = '';
      }, 4200);
    }

    dispatchCartEvent(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }
  }

  customElements.define('h-cart', HCart);
})();
